import { CHANGE_TOPIC_SUBSCRIPTION_STATUS } from '@/constants/api'
import { changeTopicSubscriptionStatus, getTopicDetailQueryOptions } from '@/utils/fetchers/topic'
import { sleep } from '@/utils/promise'
import { css } from '@emotion/react'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useMemo } from 'react'
import { useAsync } from 'react-use'
import Button, { type ButtonProps } from './Button'

export interface TopicSubscriptionToggleButtonProps extends ButtonProps {
	id: string
	noAddIcon?: boolean
}

// 处理加号图标的子组件
function AddIcon() {
	return (
		<svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M3.41675 8H14.0834"
				stroke="currentColor"
				strokeWidth="1.66667"
				strokeLinecap="round"
			/>
			<path
				d="M8.75 2.6665L8.75 13.3332"
				stroke="currentColor"
				strokeWidth="1.66667"
				strokeLinecap="round"
			/>
		</svg>
	)
}

// 处理按钮状态的子组件
function SubscriptionButton({
	isSubscribed,
	isLoading,
	handleToggle,
	noAddIcon,
	...props
}: {
	isSubscribed: boolean
	isLoading: boolean
	handleToggle: () => void
	noAddIcon?: boolean
} & ButtonProps) {
	const animationProps = useMemo(
		() => ({
			initial: { opacity: 0 },
			animate: { opacity: 1 },
			exit: { opacity: 0 },
		}),
		[]
	)

	return (
		<motion.div key={isSubscribed ? 'followed' : 'follow'} {...animationProps}>
			<Button
				loading={isLoading}
				disabled={isLoading}
				onClick={handleToggle}
				appearance={isSubscribed ? 'ghost' : 'primary'}
				css={css`
					&.jk-btn.jk-btn-md {
						min-width: unset;
						width: 100px;
						padding: 8px 20px;
						border-radius: 32px;
						height: 38px;

						/* Body 2 */
						font-size: 18px;
						line-height: 24px; /* 133.333% */
						gap: 4px;
						color: var(--tint-primary);

						${isSubscribed &&
						css`
							color: var(--tint-secondary);
						`}
					}
				`}
				{...props}
			>
				{!isSubscribed && !noAddIcon && <AddIcon />}
				{isSubscribed ? '已加入' : '加入'}
			</Button>
		</motion.div>
	)
}

export default function TopicSubscriptionToggleButton({
	id,
	noAddIcon = false,
	...props
}: TopicSubscriptionToggleButtonProps) {
	const {
		data,
		refetch,
		isLoading: isDataLoading,
	} = useSuspenseQuery(getTopicDetailQueryOptions(id))

	const isSubscribed = data.subscribedStatusRawValue > 0
	const { mutateAsync: toggleSubscription, isPending: isTogglingSubscriptionLoading } = useMutation(
		{
			mutationKey: [CHANGE_TOPIC_SUBSCRIPTION_STATUS, id],
			mutationFn: (isSubscribe: boolean) => changeTopicSubscriptionStatus(id, isSubscribe),
			onSettled: () => void refetch(),
		}
	)

	const { value: isLoading = false } = useAsync(
		async () => sleep(250).then(() => isTogglingSubscriptionLoading || isDataLoading),
		[isTogglingSubscriptionLoading || isDataLoading]
	)

	const handleToggle = useCallback(async () => {
		await toggleSubscription(!isSubscribed)
	}, [isSubscribed, toggleSubscription])

	return (
		<AnimatePresence mode="wait">
			<SubscriptionButton
				isSubscribed={isSubscribed}
				isLoading={isLoading}
				handleToggle={handleToggle}
				noAddIcon={noAddIcon}
				{...props}
			/>
		</AnimatePresence>
	)
}
